import {useMaterialType} from 'components/customer/Product/Specs/helpers/useMaterialType';
import {getValidStructure} from 'hooks/QFP/Shared';
import React, {useCallback, useState} from 'react';
import {MaterialFormFieldOptions} from 'shared/types/materials';
import {Search} from 'components/customer/Materials/Search';
import styled, {css} from 'styled-components';
import {nonTouchDevices, touchDevices} from 'shared/Breakpoints';
import MaterialDetails from 'components/customer/Materials/MaterialDetails';
import {MaterialSpecs} from 'components/customer/Materials/MaterialSpecs';
import {MaterialButton} from 'components/customer/AdvancedMaterials';
import {Page} from 'store/customer/entity/Page';
import {useResizeObserver} from 'shared/helpers/useResizeObserver';
import {useAppSelector} from 'store/customer';
import {getMaterial} from 'components/customer/Materials/store/selectors/materialSelector';
import {getEdge} from 'components/customer/Materials/store/selectors/edgeSelector';
import {selectHasEdges} from 'components/customer/AdvancedMaterials/store/materialSlice';
import {getCabinetFields} from 'shared/helpers';
import {useProductContext} from 'contexts';

interface MaterialSearchProps {
    field: MaterialFormFieldOptions;
    isExterior: boolean;
    isMiniBrowser?: boolean;
    productInfo?: Partial<ReturnType<typeof getValidStructure>>;
    page?: Page;
}

export const MaterialSearch = ({
    isExterior,
    field,
    isMiniBrowser,
    productInfo,
    page = Page.PRODUCT,
}: MaterialSearchProps) => {
    const {materialType} = useMaterialType(isExterior);
    const {formStructure} = useProductContext();
    const hasEdges = useAppSelector(selectHasEdges);
    const selectedMaterial = useAppSelector((state) =>
        getMaterial(state, materialType)
    );
    const selectedEdge = useAppSelector((state) =>
        getEdge(state, materialType)
    );

    const isRestricted =
        page === Page.PRODUCT &&
        Boolean(selectedMaterial?.is_hidden || selectedEdge?.is_hidden);

    const [showMaterialSearch, setShowMaterialSearch] = useState(isRestricted);
    const [stack, setStack] = useState(false);

    let showEdgeMaterials = hasEdges;

    if (Array.isArray(formStructure) && formStructure.length > 0) {
        const materialFields = getCabinetFields(formStructure) as {
            hasEdgeMaterial: boolean;
        };

        showEdgeMaterials = hasEdges && materialFields?.hasEdgeMaterial;
    }

    const container = useResizeObserver((entries) => {
        if (entries.length > 0) {
            const {contentRect} = entries[0];
            const {width} = contentRect;

            setStack(width < 296);
        }
    });

    const handleShow = useCallback(() => {
        setShowMaterialSearch(true);
    }, []);

    return (
        <SearchContainer $stack={stack} $isMiniBrowser={isMiniBrowser}>
            {showMaterialSearch ? (
                <Search
                    isMiniBrowser={isMiniBrowser}
                    field={field}
                    hasDoor={
                        (isExterior &&
                            (productInfo?.hasDoors ||
                                productInfo?.hasDrawers)) ||
                        false
                    }
                    materialType={materialType}
                    page={page}
                />
            ) : (
                <SpecsContainer
                    $isMiniBrowser={isMiniBrowser}
                    ref={container}
                    $stack={stack}>
                    <div>
                        <MaterialDetails
                            materialType={materialType}
                            hasDoor={
                                (isExterior &&
                                    (productInfo?.hasDoors ||
                                        productInfo?.hasDrawers)) ||
                                false
                            }
                            hasEdge={showEdgeMaterials}
                            noGap
                        />
                        <MaterialSpecs
                            field={field}
                            type={materialType}
                            defaultSpan={6}
                        />
                    </div>
                    <QuickSearchButton
                        iconName="search-white.svg"
                        className={`button-blue simplified-material-button advanced_material_editor`}
                        onClick={handleShow}>
                        Quick Search
                    </QuickSearchButton>
                </SpecsContainer>
            )}
        </SearchContainer>
    );
};

export interface ResponsiveProps {
    $isMiniBrowser?: boolean;
    $stack?: boolean;
    $isExterior?: boolean;
}

const SearchContainer = styled.div<ResponsiveProps>`
    flex-grow: 1;
    display: flex;
    align-items: center;
    width: 100%;

    > div:first-child {
        flex-grow: 1;
    }

    ${nonTouchDevices<ResponsiveProps>`
        ${({$stack, $isMiniBrowser}) => {
            if ($stack && !$isMiniBrowser) {
                return `
                align-items: initial;
            `;
            }
        }}
    `}
`;

const MobileSpecsContainer = css`
    flex-grow: 1;
    flex-direction: column;
    margin-top: 10px;
    gap: 0;

    > button {
        width: 50% !important;
        margin: initial !important;
    }
`;

const SpecsContainer = styled.div<ResponsiveProps>`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 15px;

    ${({$isMiniBrowser}) => $isMiniBrowser && MobileSpecsContainer}

    ${touchDevices`
        ${MobileSpecsContainer}
    `}

    ${nonTouchDevices<ResponsiveProps>`
        ${({$stack, $isMiniBrowser}) => {
            if ($stack && !$isMiniBrowser) {
                return `
                    flex-direction: column;

                    > button {
                        margin: initial !important;
                    }
                `;
            }
        }}
    `}
`;

const QuickSearchButton = styled(MaterialButton)`
    width: 150px !important;
    min-width: 140px;
    font-size: 0.8em !important;
`;
